import { Node } from '@xyflow/react'

// Map blueprint node types to actual ReactFlow node types
const mapBlueprintNodeType = (blueprintType: string): string => {
    const typeMap: Record<string, string> = {
        'FileResourceNode': 'noderesource',
        'FormWizardNode': 'nodeformwizard',
        'NodeIntellica': 'nodeintellica',
        'DocumentNode': 'nodedocument',
        'ResearcherNode': 'noderesearcher',
        'ChatNode': 'nodechat',
        'SmartNotesNode': 'nodesmartnotes',
        'CommandChatNode': 'commandnodechat',
        'NodeDocumentDesigner': 'nodedocumentdesigner',
        'NodeDiagrams': 'nodediagrams',
        'FileMinerNode': 'nodefileminer',
        'CompareFilesNode': 'nodecomparefiles',
        'JavaScriptNode': 'nodejavascript',
        'QuickStartNode': 'nodequickstart',
        'nodequickstartdynamic': 'nodequickstartdynamic',
        'ImageGeneratorNode': 'nodeimagegenerator',
        'NodeResourcePDF': 'noderesourcepdf',
        'NodeStrategy': 'nodestrategy',
        'NodeRecommendator': 'noderecommendator',
        'PresenceNode': 'nodepresence',
        'AgencyNode': 'nodeagency',
        'NodeGlobalConstants': 'nodeglobalconstants',
        'nodeknowledgeset': 'nodeknowledgeset',
        'NodeComposite': 'nodecomposite',
        'NodeWebExtract': 'nodewebextract',
        'NodeFacilitator': 'nodefacilitator',
        'NodeResearcher': 'noderesearcher',
        'NodeAgent': 'nodeagent',
        'NodeDomainKnowledge': 'nodedomainknowledge',
        'NodeHtmlFields': 'nodehtmlfields',
        'NodeGroupBlueprint': 'nodegroupblueprint',
        'NodeWorkflow': 'nodeworkflow'
    }

    // Return mapped type or fallback to lowercase version or original
    return typeMap[blueprintType] || blueprintType.toLowerCase() || 'default'
}

export interface BlueprintContainerResult {
    containerNode: Node
    blueprintNodes: Node[]
    allNodes: Node[]
}

export const createBlueprintContainer = (
    blueprint: any,
    position: { x: number; y: number } = { x: 100, y: 100 }
): BlueprintContainerResult => {
    // Create the container group node first
    const containerId = `blueprint_${blueprint.id}_${Date.now()}`    // Create nodes from blueprint definition as child nodes
    const blueprintNodes: Node[] = []

    if (blueprint.nodeDefinitions && Array.isArray(blueprint.nodeDefinitions)) {
        console.log('Processing nodeDefinitions:', blueprint.nodeDefinitions)
        blueprint.nodeDefinitions.forEach((nodeDef: any, index: number) => {
            const nodeId = `${blueprint.id}_node_${index}_${Date.now()}`            // Handle both old and new blueprint formats
            // Blueprint editor saves as: type: node.data.nodeType
            const rawNodeType = nodeDef.type || nodeDef.nodeType || 'default'
            const nodeType = mapBlueprintNodeType(rawNodeType) // Map to correct ReactFlow node type
            const nodeLabel = nodeDef.label || `Node ${index + 1}`
            const nodeData = nodeDef.configuration || nodeDef.data || {}

            console.log(`Creating child node ${index + 1}:`, {
                nodeId,
                rawNodeType,
                mappedNodeType: nodeType,
                nodeLabel,
                originalNodeDef: nodeDef,
                hasType: !!nodeDef.type,
                hasNodeType: !!nodeDef.nodeType,
                hasConfiguration: !!nodeDef.configuration,
                hasData: !!nodeDef.data
            })

            const node: Node = {
                id: nodeId,
                type: nodeType,
                position: {
                    x: 20 + (index % 3) * 200, // relative position inside container
                    y: 60 + Math.floor(index / 3) * 120
                },
                data: {
                    ...nodeData,
                    label: nodeLabel,
                    // Ensure node has required properties
                    informationClassification: nodeData?.informationClassification || 'Internal',
                    createdBy: 'Blueprint',
                    createdAt: new Date().toISOString()
                },
                parentId: containerId, // This makes it a child of the container
                extent: 'parent', // Keep child nodes within the parent
                draggable: true
            }

            console.log(`Final child node ${index + 1} structure:`, {
                id: node.id,
                type: node.type,
                parentId: node.parentId,
                extent: node.extent,
                position: node.position,
                hasParentId: !!node.parentId,
                parentIdValue: node.parentId
            })

            blueprintNodes.push(node)
            console.log(`Added blueprint child node ${index + 1}:`, node)
        })
    } else {
        console.warn('No nodeDefinitions found in blueprint:', blueprint)
        console.log('Blueprint keys:', Object.keys(blueprint))
        console.log('Full blueprint structure:', JSON.stringify(blueprint, null, 2))

        // Create a test node to verify the container works
        const testNodeId = `${blueprint.id}_test_${Date.now()}`
        const testNode: Node = {
            id: testNodeId,
            type: 'default',
            position: { x: 50, y: 80 },
            data: {
                label: 'Test Node',
                informationClassification: 'Internal',
                createdBy: 'Blueprint Test',
                createdAt: new Date().toISOString()
            },
            parentId: containerId,
            extent: 'parent',
            draggable: true
        }
        blueprintNodes.push(testNode)
        console.log('Added test node for debugging:', testNode)
    }    // Calculate container dimensions based on child nodes bounding box
    let containerWidth = 2000 // Default minimum width
    let containerHeight = 2000 // Default minimum height

    if (blueprintNodes.length > 0) {
        // Calculate bounding box from top-left to bottom-right corners
        const padding = 40 // Padding around child nodes
        const defaultNodeWidth = 2000 // Default node width
        const defaultNodeHeight = 2000 // Default node height

        // Find the bounds of all child nodes
        let minX = Number.MAX_VALUE
        let minY = Number.MAX_VALUE
        let maxX = Number.MIN_VALUE
        let maxY = Number.MIN_VALUE
        blueprintNodes.forEach(node => {
            const nodeX = node.position.x
            const nodeY = node.position.y
            // Handle width and height that might be strings or numbers
            const nodeWidth = typeof node.style?.width === 'number' ? node.style.width :
                typeof node.style?.width === 'string' ? parseInt(node.style.width) || defaultNodeWidth :
                    defaultNodeWidth
            const nodeHeight = typeof node.style?.height === 'number' ? node.style.height :
                typeof node.style?.height === 'string' ? parseInt(node.style.height) || defaultNodeHeight :
                    defaultNodeHeight

            // Top-left corner
            minX = Math.min(minX, nodeX)
            minY = Math.min(minY, nodeY)

            // Bottom-right corner (position + size)
            maxX = Math.max(maxX, nodeX + nodeWidth)
            maxY = Math.max(maxY, nodeY + nodeHeight)
        })

        // Calculate container size based on bounding box plus padding
        containerWidth = Math.max(400, (maxX - minX) + (padding * 2))
        containerHeight = Math.max(200, (maxY - minY) + (padding * 2))

        console.log('Container size calculation:', {
            childNodesCount: blueprintNodes.length,
            bounds: { minX, minY, maxX, maxY },
            boundingBoxSize: { width: maxX - minX, height: maxY - minY },
            withPadding: { width: containerWidth, height: containerHeight },
            padding
        })
    }// Create the container as a custom group node that supports resizing
    const containerNode: Node = {
        id: containerId,
        type: 'groupnode', // Use custom GroupNode component that supports resizing
        position,
        data: {
            label: blueprint.name || 'Blueprint Container',
            description: `Blueprint: ${blueprint.name}`,
            isExpanded: true,
            childNodes: blueprintNodes.map(n => n.id),
            groupType: 'parallel' as const,
            backgroundColor: 'rgba(254, 243, 199, 0.8)', // amber-100 with transparency
            borderColor: '#f59e0b', // amber-500
            informationClassification: 'Blueprint',
            createdBy: 'System',
            createdAt: new Date().toISOString()
        },
        width: containerWidth,
        height: containerHeight,
        draggable: true
    }

    console.log('Final container node structure:', {
        id: containerNode.id,
        type: containerNode.type,
        position: containerNode.position,
        style: containerNode.style,
        hasChildren: blueprintNodes.length > 0,
        childCount: blueprintNodes.length
    })

    console.log('All child nodes parentId check:', blueprintNodes.map(n => ({
        id: n.id,
        parentId: n.parentId,
        hasCorrectParent: n.parentId === containerId
    })))

    return {
        containerNode,
        blueprintNodes,
        allNodes: [containerNode, ...blueprintNodes]
    }
}
